import { useAppDispatch } from "../../../app/hooks";
import AddIcon from "@mui/icons-material/Add";
import VisibilityIcon from '@mui/icons-material/Visibility';
import AppRegistrationIcon from '@mui/icons-material/AppRegistration';
import { useSelector } from "react-redux";
import { classroomStudentsSlice, retrieveClassroomStudents, selectClassroomStudents } from "../../../redux/classroom/classroomStudentsSlice";
import { useNavigate } from "react-router";
import { selectUser } from "../../../redux/user/userSlice";
import { anyPermission, hasPermission, PermissionId } from "../../../app/permissions";
import { routeStudent } from "../../../routes/student";
import { routeClassroomStudentAdd } from "../../../routes/classroom";
import { Box, Button, ButtonGroup, Link, Stack, Table, TableBody, TableCell, TableHead, TableRow, Tooltip, Typography } from "@mui/material";
import UserStack from "../../../features/user/UserStack";
import { studentSlice } from "../../../redux/student/studentSlice";
import PrizaAvatarGrid from "../../../features/base/grid/PrizaAvatarGrid";
import { assertThumbnailUrl, getBackendTypstUrl, isoToBrt, LoadState } from "../../../redux/common";
import { useEffect, useMemo, useState } from "react";
import { isTokenReady } from "../../../features/user/UserAvatar";

import GridOnIcon from '@mui/icons-material/GridOn';
import DnsIcon from '@mui/icons-material/Dns';
import BadgedAvatar from "../../../features/base/BadgedAvatar";

interface ContentListProps {
    schoolId: string;
    classroomId: string;
}

const StudentsList = ({ schoolId, classroomId }: ContentListProps) => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const classroomStudents = useSelector(selectClassroomStudents);
    const user = useSelector(selectUser);
    const [table, setTable] = useState(true);

    const request = useMemo(() => ({ schoolId, classroomId }), [schoolId, classroomId]);
    useEffect(() => {
        dispatch(classroomStudentsSlice.actions.check(request));
        if (classroomStudents.state.state == LoadState.NotStarted && isTokenReady(user)) {
            dispatch(retrieveClassroomStudents(request));
        }
    }, [request, classroomStudents]);

    const list = classroomStudents.list || [];
    const addStudent = () => {
        navigate(routeClassroomStudentAdd(schoolId, classroomId));
    };

    const mapped = list.map((item) => {
        const click = () => {
            dispatch(studentSlice.actions.clean());
            navigate(routeStudent(schoolId, item.id, classroomId));
        };
        return {
            image: assertThumbnailUrl(item.url),
            title: item.nickname || item.name,
            subtitle: item.name,
            item: item,
            click: click
        };
    });

    const perm: boolean = hasPermission(user.priza.info, PermissionId.SCHOOL_ADMIN, schoolId)
            || anyPermission(user.priza.info, classroomId, [ PermissionId.CLASSROOM_ADMIN, PermissionId.CLASSROOM_TEACHER ]);

    const buttons = [];
    buttons.push({
        label: table ? <DnsIcon /> : <GridOnIcon />,
        click: () => {
            setTable(!table);
        }
    });
    if (perm) {
        buttons.push({
            label: <Tooltip title="Alterar lista de alunos (adicinar ou remover)" ><AddIcon /></Tooltip>,
            click: () => { addStudent(); }
        });
    }

    const buildContent = !table ? undefined : () => {
        if (list.length == 0) {
            return <Box sx={{ p: 2 }}>
                <Typography variant="body1" component="div">
                    Nenhum aluno associado a esta sala de aula.
                </Typography>
            </Box>;
        }
        return <>
            <Table>
                <TableHead>
                    <TableRow>
                        <TableCell align="left" sx={{
                                color: (theme) => theme.palette.primary.contrastText,
                                backgroundColor: (theme) => theme.palette.primary.main,
                        }}>Aluno</TableCell>
                        <TableCell align="left" sx={{
                                color: (theme) => theme.palette.primary.contrastText,
                                backgroundColor: (theme) => theme.palette.primary.main,
                        }}>Apelido</TableCell>
                        <TableCell align="left" sx={{
                                color: (theme) => theme.palette.primary.contrastText,
                                backgroundColor: (theme) => theme.palette.primary.main,
                        }}>Nascimento</TableCell>
                        <TableCell align="left" sx={{
                                color: (theme) => theme.palette.primary.contrastText,
                                backgroundColor: (theme) => theme.palette.primary.main,
                        }}>Matricula</TableCell>
                        <TableCell align="left" sx={{
                                color: (theme) => theme.palette.primary.contrastText,
                                backgroundColor: (theme) => theme.palette.primary.main,
                        }}>Responsáveis</TableCell>
                        <TableCell align="left" sx={{
                                color: (theme) => theme.palette.primary.contrastText,
                                backgroundColor: (theme) => theme.palette.primary.main,
                        }}>Ações</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {list.map((student) => {
                        const sx = {
                            padding: "4px 8px",
                        };
                        return <TableRow key={student.id} hover
                            // onClick={() => {
                            //     dispatch(studentSlice.actions.clean());
                            //     navigate(routeStudent(schoolId, item.id, classroomId));
                            // }}
                            >
                            <TableCell sx={sx}>
                                <Stack direction="row" alignItems="center">
                                    <BadgedAvatar
                                        title={student.name}
                                        avatar={assertThumbnailUrl(student.url)}
                                        size="32px"
                                        marginRight={1}
                                    />
                                    {student.name}
                                </Stack>
                            </TableCell>
                            <TableCell sx={sx}>{student.nickname || student.name}</TableCell>
                            <TableCell sx={sx}>{isoToBrt(student.birthDate)}</TableCell>
                            <TableCell sx={sx}>{student.schoolNumber}</TableCell>
                            <TableCell sx={sx}>
                                <UserStack users={student.family} />
                            </TableCell>
                            <TableCell sx={sx}>
                                <ButtonGroup variant="outlined" size="small">
                                    <Tooltip title="Visualizar detalhes do aluno">
                                        <Button onClick={() => {
                                            dispatch(studentSlice.actions.clean());
                                            navigate(routeStudent(schoolId, student.id, classroomId));
                                        }}>
                                            <VisibilityIcon />
                                        </Button>
                                    </Tooltip>
                                    <Tooltip title="Convite para os pais">
                                        <Link href={`${getBackendTypstUrl()}/api/invite/student/${student.id}`}
                                            target="_blank" rel="noopener noreferrer"
                                            style={{ textDecoration: 'none' }}>
                                            <Button>
                                                <AppRegistrationIcon />
                                            </Button>
                                        </Link>
                                    </Tooltip>
                                </ButtonGroup>
                            </TableCell>
                        </TableRow>;
                    })}
                </TableBody>
            </Table>
        </>;
    };

    return <PrizaAvatarGrid list={mapped}
        toolbar={{
            label: `Alunos (${mapped.length})`,
            buttons: buttons
        }}
        state={classroomStudents.state}
        retry={() => { dispatch(classroomStudentsSlice.actions.clean()); }}
        buildContent={buildContent}
        messages={{
            empty: "Nenhum aluno associado a esta sala de aula.",
            error: "Erro ao carregar alunos."
        }}
    />;

};

export default StudentsList;